import { useState, useMemo, useCallback, useEffect, useDeferredValue } from 'react';
import {
  useRemoveCustomer,
  useCustomerList,
  useToggleCustomerStatus,
  useUpdateCustomer,
  useCreateCustomer,
  useResetCustomerAdminPassword,
} from '../hooks';
import type { CustomerFilters, CustomerRecord, CustomerCreateValues } from '../types';
import { CustomerTable } from '../components/CustomerTable';
import { CustomerFormModal } from '../components/CustomerFormModal';
import { debounce } from '@shared/utils/debounce';

export function CustomerListPage() {
  const [filters, setFilters] = useState<CustomerFilters>({ status: 'all', search: '' });
  const deferredFilters = useDeferredValue(filters);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CustomerRecord | null>(null);

  const { data = [], isLoading } = useCustomerList(deferredFilters);
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const removeMutation = useRemoveCustomer();
  const toggleStatusMutation = useToggleCustomerStatus();
  const resetPasswordMutation = useResetCustomerAdminPassword();

  const isBusy =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    removeMutation.isLoading ||
    toggleStatusMutation.isLoading;

  const openEditModal = useCallback((record: CustomerRecord) => {
    setEditingRecord(record);
    setIsCreateMode(false);
    setModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditingRecord(null);
    setIsCreateMode(true);
    setModalOpen(true);
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Xác nhận xóa ${selectedIds.length} khách hàng?`)) return;

    await Promise.all(selectedIds.map((id) => removeMutation.mutateAsync(id)));
    setSelectedIds([]);
  }, [selectedIds, removeMutation]);

  const handleDelete = useCallback(
    (record: CustomerRecord) => {
      if (!confirm(`Bạn chắc chắn muốn xóa khách hàng ${record.name}? Hành động này sẽ vô hiệu hóa tài khoản Admin Công Ty tương ứng.`))
        return;
      removeMutation.mutate(record.id);
    },
    [removeMutation]
  );

  const handleToggleStatus = useCallback(
    (record: CustomerRecord) => {
      const nextStatus = record.status === 'active' ? 'inactive' : 'active';
      const action = nextStatus === 'active' ? 'kích hoạt' : 'vô hiệu hóa';
      if (!confirm(`Bạn chắc chắn muốn ${action} khách hàng ${record.name}?`)) return;
      toggleStatusMutation.mutate({ id: record.id, status: nextStatus });
    },
    [toggleStatusMutation]
  );

  const handleResetPassword = useCallback(
    (record: CustomerRecord) => {
      if (!confirm(`Bạn chắc chắn muốn reset mật khẩu cho Admin Công Ty của ${record.name}?`)) return;
      resetPasswordMutation.mutate(record.id);
    },
    [resetPasswordMutation]
  );

  // Debounce filter changes
  const debouncedHandleFilterChange = useMemo(
    () =>
      debounce((next: CustomerFilters) => {
        setFilters(next);
      }, 300),
    []
  );

  const handleFilterChange = useCallback(
    (next: CustomerFilters) => {
      debouncedHandleFilterChange(next);
    },
    [debouncedHandleFilterChange]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedHandleFilterChange.cancel();
    };
  }, [debouncedHandleFilterChange]);

  
  return (
    <div className="card">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="card-title mb-1">Quản lý khách hàng</h3>
          <p className="card-subtitle mb-0">
            Quản lý thông tin khách hàng (tenants) đã đăng ký hoặc mua gói dịch vụ.
          </p>
        </div>
        <div className="toolbar-actions" style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleCreate}
            disabled={isBusy}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-plus" style={{ fontSize: '16px', marginRight: '6px' }} />
            Thêm mới
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={selectedIds.length === 0 || removeMutation.isLoading}
            onClick={handleBulkDelete}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-trash" style={{ fontSize: '16px', marginRight: '6px' }} />
            Xóa đã chọn
          </button>
        </div>
      </div>

      <CustomerTable
        data={data}
        isLoading={isLoading || isBusy}
        filters={filters}
        onFiltersChange={handleFilterChange}
        onEdit={openEditModal}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
        onResetPassword={handleResetPassword}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <CustomerFormModal
        open={isModalOpen}
        mode={isCreateMode ? 'create' : 'edit'}
        onClose={() => {
          setModalOpen(false);
          setEditingRecord(null);
          setIsCreateMode(false);
        }}
        initialData={editingRecord}
        isSubmitting={isBusy}
        onSubmitCreate={async (payload: CustomerCreateValues) => {
          await createMutation.mutateAsync(payload);
          setModalOpen(false);
          setIsCreateMode(false);
        }}
        onSubmitUpdate={async (id, payload) => {
          await updateMutation.mutateAsync({ id, payload });
          setModalOpen(false);
          setIsCreateMode(false);
        }}
      />
    </div>
  );
}

